import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { RouteRecommendationService } from '../../../services/route-recommendation.service';
import { IRouteRecommendationItinerary } from '../../../interfaces/route-recommendations/route-recommendation-itinerary';
import { ActivatedRoute, ParamMap, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { ExternalMapHelper } from '../../../utils/external-map.helper';
import { LatLng } from '../../map-control/marker.point';
import { RouteItineraryTravelPosition } from '../../../enums/algo-travel-position.enum';
import { RouteItineraryDateSelectorComponent } from './components/route-itinerary-date-selector/route-itinerary-date-selector.component';
import { RouteItineraryHeaderComponent } from './components/route-itinerary-header/route-itinerary-header.component';
import { RouteItineraryDayBreakComponent } from './components/route-itinerary-day-break/route-itinerary-day-break.component';
import { RouteItineraryVerticalDotsContainerComponent } from './components/route-itinerary-vertical-dots-container/route-itinerary-vertical-dots-container.component';
import { RouteItineraryDirectionLinkComponent } from './components/route-itinerary-direction-link/route-itinerary-direction-link.component';
import { RouteItineraryStartEndDetailComponent } from './components/route-itinerary-start-end-detail/route-itinerary-start-end-detail.component';
import { RouteItineraryTravelGuideComponent } from './components/route-itinerary-travel-guide/route-itinerary-travel-guide.component';
import { RouteItineraryTaskDetailComponent } from './components/route-itinerary-task-detail/route-itinerary-task-detail.component';
import { RouteItineraryTaskAddressDetailComponent } from './components/route-itinerary-task-address-detail/route-itinerary-task-address-detail.component';
import { RouteItineraryTaskExpanderComponent } from './components/route-itinerary-task-expander/route-itinerary-task-expander.component';
import { RouteItineraryAlertComponent } from './components/route-itinerary-alert/route-itinerary-alert.component';
import { IRouteAlert } from '../../../interfaces/route-recommendations/route-alert';
import { CommonModule } from '@angular/common';
import { RoutesAnalyticsService } from 'src/app/services/product-analytics/routes-analytics.service';

@Component({
  selector: 'route-itinerary',
  templateUrl: './route-itinerary.component.html',
  styleUrls: ['./route-itinerary.component.scss', './common/scss/route-itinerary-common.scss'],
  standalone: true,
  imports: [
    RouteItineraryDateSelectorComponent,
    RouteItineraryHeaderComponent,
    RouteItineraryDayBreakComponent,
    RouteItineraryVerticalDotsContainerComponent,
    RouteItineraryDirectionLinkComponent,
    RouteItineraryStartEndDetailComponent,
    RouteItineraryTravelGuideComponent,
    RouteItineraryTaskDetailComponent,
    RouteItineraryTaskAddressDetailComponent,
    RouteItineraryTaskExpanderComponent,
    RouteItineraryAlertComponent,
    CommonModule,
    RouterModule,
  ],
})
export class RouteItineraryComponent implements OnInit, OnDestroy {
  private routeSub: Subscription | undefined;

  primaryRouteId: number;
  accessToken: string;
  itinerary: IRouteRecommendationItinerary;
  hideDateSelector: boolean = false;
  expandedTaskIndex: number | null = null;

  @Input() fullWidth: boolean = false;
  @Input() routeId: number;
  @Input() routeGrabbed: boolean = false;

  get visibleAlerts(): IRouteAlert[] {
    return this.itinerary?.routeAlerts ?? [];
  }

  @Output() alternateRouteSelected: EventEmitter<number> = new EventEmitter();
  @Output() itineraryLoaded: EventEmitter<void> = new EventEmitter();

  constructor(
    private routeRecommendationService: RouteRecommendationService,
    private activatedRoute: ActivatedRoute,
    public externalMapHelper: ExternalMapHelper,
    private routeAnalyticsService: RoutesAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.routeSub = this.activatedRoute.paramMap.subscribe(async (params: ParamMap) => {
      if (this.routeId == null) {
        this.routeId = Number(params.get('id'));
      }

      this.primaryRouteId = this.routeId;

      const hideAlternateDatesParam =
        this.activatedRoute.snapshot.queryParamMap.get('hideAlternateDates');

      this.hideDateSelector = hideAlternateDatesParam == 'true';

      this.accessToken = this.activatedRoute.snapshot.queryParamMap.get('accessToken');

      await this.getRouteItinerary(this.routeId);
    });
  }

  async getRouteItinerary(routeId: number): Promise<void> {
    await this.routeRecommendationService
      .getItinerary(routeId, this.accessToken)
      .then((itinerary) => {
        this.itinerary = itinerary;
        this.itineraryLoaded.emit();
      });
  }

  async dateChangedInRouteItineraryComponent(routeId: number): Promise<void> {
    this.routeAnalyticsService.sendRouteTimingsChangeEvent(this.primaryRouteId, routeId);
    //update the itinerary in the popup
    await this.getRouteItinerary(routeId);
    this.alternateRouteSelected.emit(routeId);
  }

  ngOnDestroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  public get supplierHomeCoordinates(): LatLng {
    return {
      lat: this.itinerary?.supplierDetail?.latitude,
      lng: this.itinerary?.supplierDetail?.longitude,
    } as LatLng;
  }

  public getTaskPickupCoordinates(taskIndex: number): LatLng {
    const location = this.itinerary?.recommendationTasks[taskIndex].task.pickupLocation;
    return {
      lat: location.latitude,
      lng: location.longitude,
    };
  }

  getTaskDropOffCoordinates(taskIndex: number): LatLng {
    const location = this.itinerary?.recommendationTasks[taskIndex].task.dropoffLocation;
    return {
      lat: location.latitude,
      lng: location.longitude,
    };
  }

  toggleTaskExpander(index: number): void {
    this.expandedTaskIndex = this.expandedTaskIndex === index ? null : index;
  }

  viewTaskDirections(index: number): void {
    this.routeAnalyticsService.sendGoogleDirectionsEvent();
    const pickup = this.getTaskPickupCoordinates(index);
    const dropOff = this.getTaskDropOffCoordinates(index);

    const task = this.itinerary?.recommendationTasks[index];

    if (task?.hasCarryover) {
      this.externalMapHelper.openDirections(
        pickup.lat,
        pickup.lng,
        this.supplierHomeCoordinates.lat,
        this.supplierHomeCoordinates.lng,
      );
    } else {
      this.externalMapHelper.openDirections(pickup.lat, pickup.lng, dropOff.lat, dropOff.lng);
    }
  }

  openCarryoverReturnLink(index: number): void {
    this.routeAnalyticsService.sendGoogleDirectionsEvent();
    const dropOff = this.getTaskDropOffCoordinates(index);
    this.externalMapHelper.openDirections(
      this.supplierHomeCoordinates.lat,
      this.supplierHomeCoordinates.lng,
      dropOff.lat,
      dropOff.lng,
    );
  }

  openExternalLink(url: string): void {
    if (!url) return;
    this.routeAnalyticsService.sendGoogleDirectionsEvent();
    window.open(url, '_blank');
  }

  getCarryOverSequenceNumber(currentIndex: number): number {
    if (currentIndex < 0) {
      return 0;
    }

    let carryOverSequence = 0;
    for (let i = 0; i <= currentIndex; i++) {
      if (
        this.itinerary.recommendationTasks[i] &&
        this.itinerary.recommendationTasks[i].hasCarryover
      ) {
        carryOverSequence++;
      }
    }

    return carryOverSequence;
  }

  protected readonly RouteItineraryTravelPosition = RouteItineraryTravelPosition;
}
